import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';

export default function Navbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Navbar tightens up and gets a stronger glow once the visitor has
  // scrolled past the hero — a small cue that the page is alive.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  // Logged-in users (dashboard/admin) have their own left Sidebar with
  // Dashboard/Profile/Logout links already — so on mobile the hamburger
  // should open THAT sidebar, not this navbar's own dropdown.
  const handleHamburgerClick = () => {
    if (user && onMenuClick) {
      onMenuClick();
    } else {
      setMenuOpen(!menuOpen);
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <Link to="/" className="navbar-brand">
        <img src="/logo.png" alt="Triveni Pay" className="navbar-logo-img" />
      </Link>

      {/* Everything after the logo — links, bell, username, theme toggle,
          hamburger — lives in ONE flex cluster so it all sits together on
          the right with a normal gap, instead of navbar's space-between
          pushing the theme toggle off into a corner by itself. */}
      <div className="navbar-end">
        {/* This dropdown is only ever opened on mobile for logged-OUT visitors
            (marketing site). Logged-in users get the Sidebar instead, so
            `menuOpen` never toggles for them (see handleHamburgerClick). */}
        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {!user ? (
            <>
              <div className="nav-links-plain">
                <Link to="/" className="nav-link" onClick={() => setMenuOpen(false)}>Home</Link>
                <Link to="/about" className="nav-link" onClick={() => setMenuOpen(false)}>About</Link>
                <Link to="/services" className="nav-link" onClick={() => setMenuOpen(false)}>Services</Link>
                <Link to="/income" className="nav-link" onClick={() => setMenuOpen(false)}>Income Plan</Link>
                <Link to="/terms" className="nav-link" onClick={() => setMenuOpen(false)}>Terms</Link>
                <Link to="/contact" className="nav-link" onClick={() => setMenuOpen(false)}>Contact</Link>
              </div>
              <div className="nav-links-auth">
                <Link to="/login" className="btn btn-outline btn-sm" onClick={() => setMenuOpen(false)}>Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm" onClick={() => setMenuOpen(false)}>Join Now</Link>
              </div>
            </>
          ) : (
            <>
              <NotificationBell />
              <span className="nav-link" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {user.name?.split(' ')[0]}
              </span>
              <button className="btn btn-outline btn-sm" onClick={handleLogout}>Logout</button>
            </>
          )}
        </div>

        {/* Kept OUTSIDE .nav-links on purpose so it's always visible — including
            on mobile for logged-in users, whose .nav-links dropdown never opens
            (the hamburger opens the Sidebar for them instead). */}
        <div className="navbar-right-controls">
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          <div className="hamburger" onClick={handleHamburgerClick}>
            <span style={menuOpen ? { transform: 'rotate(45deg) translateY(7px)' } : {}}></span>
            <span style={menuOpen ? { opacity: 0 } : {}}></span>
            <span style={menuOpen ? { transform: 'rotate(-45deg) translateY(-7px)' } : {}}></span>
          </div>
        </div>
      </div>
    </nav>
  );
}